import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import React, {type LegacyRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {LabelRequiredOptional} from "./label-required-optional.tsx";

import infoIcon from "@phosphor-icons/core/regular/info.svg";

type Props = React.ComponentProps<typeof TextField> & {
    label: string;
    description?: string;
    tooltipContent?: string;
    /**
     * The error message to display.
     *
     * This is an alternative to `error` which is a boolean.
     */
    errorMessage?: string | null | undefined;
    forwardedRef?: LegacyRef<HTMLInputElement> | undefined;
};

/**
 * A text field with a label, description, and optional tooltip.
 *
 * This component is a combination of a TextField, LabelRequiredOptional, and
 * Tooltip.
 */
export function TextFieldWithInfo(props: Props) {
    const {
        // TextField props
        id,
        disabled = false,
        required,
        style,
        forwardedRef,
        error,
        // Label & tooltip props
        label,
        description,
        tooltipContent,
        errorMessage,

        // Everything else
        ...otherProps
    } = props;

    return (
        <div className={css(styles.wrapper)}>
            {tooltipContent && <MoreInfoIcon tooltipContent={tooltipContent} />}
            <View>
                <LabelRequiredOptional
                    required={!!required}
                    htmlFor={id}
                    style={[
                        styles.label,
                        !required && tooltipContent
                            ? styles.labelWithTooltipIcon
                            : null,
                    ]}
                >
                    {label}
                </LabelRequiredOptional>
                {description && (
                    <LabelSmall style={styles.description}>
                        {description}
                    </LabelSmall>
                )}
                <TextField
                    style={[styles.textField, style]}
                    id={id}
                    aria-disabled={disabled}
                    ref={forwardedRef}
                    aria-required={!!required}
                    {...otherProps}
                    error={error || !!errorMessage}
                    aria-describedby={
                        props["aria-describedby"] ||
                        (errorMessage ? `${id}-error` : undefined)
                    }
                />
                {errorMessage && (
                    <ErrorMessage id={`${id}-error`}>
                        {errorMessage}
                    </ErrorMessage>
                )}
            </View>
        </div>
    );
}

function ErrorMessage({
    children,
    id,
}: {
    children: string;
    id: string;
}): React.ReactNode {
    return (
        <strong
            id={id}
            className={css(styles.errorMessage)}
            aria-live="polite"
            role="alert"
        >
            {children}
        </strong>
    );
}

export function MoreInfoIcon({
    tooltipContent,
    style,
}: {
    tooltipContent: string;
    style?: CSSProperties;
}) {
    return (
        <Tooltip content={tooltipContent}>
            {/* Wrapper div is needed since you cannot apply "outline" to
                        a tooltip */}
            <div className={css(styles.infoIcon, style)}>
                <PhosphorIcon
                    icon={infoIcon}
                    color={semanticColor.core.foreground.neutral.subtle}
                    size="small"
                    aria-label={t`More Info`}
                />
            </div>
        </Tooltip>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        position: "relative",
    },
    description: {
        color: semanticColor.core.foreground.neutral.subtle,
        marginBlockEnd: spacing.xxxSmall_4,
    },
    label: {
        marginBlockEnd: spacing.xxxSmall_4,
    },
    labelWithTooltipIcon: {
        marginInlineEnd: spacing.large_24,
    },
    infoIcon: {
        cursor: "pointer",

        // Position the icon in the top right corner of the text field
        position: "absolute",
        top: 0,
        insetInlineEnd: 0,

        // Needed to have the icon above the label,
        zIndex: 1,

        ":focus": {
            outline: `${semanticColor.core.border.instructive.default} auto`,
        },
    },
    textField: {
        marginBlockStart: spacing.xSmall_8,
    },
    errorMessage: {
        fontWeight: "normal",
        color: semanticColor.core.foreground.critical.subtle,
        marginBlockStart: spacing.xSmall_8,
    },
});
